import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Camera, CheckCircle2, XCircle, Loader2, User } from "lucide-react";
import { adminBookingByVoucher, adminMarkArrived } from "@/lib/api";

/**
 * QrCheckInScanner — full-screen modal camera che scansiona QR con codice voucher
 * e permette di segnare "arrivato" con un tap.
 * Usa html5-qrcode via <div id> dedicato.
 */
export function QrCheckInScanner({ token, open, onClose }) {
    const [scanning, setScanning] = useState(false);
    const [found, setFound] = useState(null);      // booking result
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const scannerRef = useRef(null);
    const containerId = "qr-scanner-container";

    useEffect(() => {
        if (!open) return;
        let mounted = true;
        (async () => {
            try {
                setScanning(true);
                setError(null);
                const { Html5Qrcode } = await import("html5-qrcode");
                if (!mounted) return;
                const scanner = new Html5Qrcode(containerId, { verbose: false });
                scannerRef.current = scanner;
                await scanner.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
                    async (decodedText) => {
                        if (!mounted) return;
                        // Estrai voucher: accetta sia "VLR-XXXXXXXX" nudo sia URL
                        const match = String(decodedText).match(/VLR-[A-Z0-9]{4,}/i);
                        if (!match) return;  // ignora, continua a scansionare
                        const voucher = match[0].toUpperCase();
                        try {
                            await scanner.pause();
                        } catch {}
                        try {
                            const b = await adminBookingByVoucher(token, voucher);
                            setFound(b);
                        } catch (e) {
                            setError(`Voucher ${voucher} non trovato`);
                            setTimeout(async () => {
                                setError(null);
                                try { await scanner.resume(); } catch {}
                            }, 2000);
                        }
                    },
                    (_scanErr) => { /* ignore per-frame errors */ }
                );
            } catch (e) {
                setError("Camera non disponibile. Consenti l'accesso o usa Chrome/Safari.");
                setScanning(false);
            }
        })();
        return () => {
            mounted = false;
            (async () => {
                if (scannerRef.current) {
                    try { await scannerRef.current.stop(); } catch {}
                    try { scannerRef.current.clear(); } catch {}
                    scannerRef.current = null;
                }
            })();
        };
    }, [open, token]);

    const markArrived = async () => {
        if (!found) return;
        setBusy(true);
        try {
            await adminMarkArrived(token, found.id);
            toast.success(`✓ ${found.customer_name} check-in fatto!`);
            setFound(null);
            setTimeout(async () => {
                try { await scannerRef.current?.resume(); } catch {}
            }, 500);
        } catch { toast.error("Errore check-in"); }
        finally { setBusy(false); }
    };

    const skipAndScanAgain = async () => {
        setFound(null);
        try { await scannerRef.current?.resume(); } catch {}
    };

    if (!open) return null;
    return (
        <div data-testid="qr-scanner-modal" className="fixed inset-0 z-50 bg-black/95 flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#9AA2AD]/20">
                <div className="flex items-center gap-3 text-[#F4F2ED]">
                    <Camera size={22} className="text-[#E2231A]" />
                    <span className="font-display text-lg uppercase tracking-widest">Scanner Check-in</span>
                </div>
                <button data-testid="btn-qr-close" onClick={onClose} className="text-[#9AA2AD] hover:text-[#E2231A] p-2"><X size={24} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 flex flex-col items-center justify-start">
                {/* Camera view (mai smontato) */}
                <div id={containerId} className={`w-full max-w-md mx-auto ${found ? "hidden" : ""}`} style={{ minHeight: "60vh" }} />

                {!found && scanning && (
                    <p className="text-[#9AA2AD] text-xs uppercase tracking-widest mt-4 text-center">
                        Inquadra il QR del voucher del cliente
                    </p>
                )}

                {error && (
                    <div className="mt-4 bg-[#E2231A]/10 border border-[#E2231A] text-[#F4F2ED] px-4 py-3 text-sm">
                        <XCircle className="inline mr-2 text-[#E2231A]" size={16} /> {error}
                    </div>
                )}

                {found && (
                    <div data-testid="qr-scan-result" className="w-full max-w-md bg-[#1C2026] border border-[#E2231A] p-5 mt-2">
                        <div className="text-xs uppercase tracking-widest text-[#E2231A] mb-2 flex items-center gap-2"><CheckCircle2 size={16} /> Voucher trovato</div>
                        <div className="font-display text-3xl uppercase leading-tight">{found.voucher_code}</div>
                        <div className="mt-1 text-[10px] uppercase tracking-widest text-[#9AA2AD]">Turno <b className="text-[#F4F2ED]">#{found.turno_progressivo || "?"}</b></div>
                        <div className="mt-4 space-y-2 text-sm text-[#F4F2ED]">
                            <div className="flex items-center gap-2"><User size={14} className="text-[#9AA2AD]" /> {found.customer_name}</div>
                            <div className="text-xs text-[#9AA2AD]">{found.customer_email}</div>
                            <div className="text-xs text-[#9AA2AD]">Formula: {(found.product_id || "").replace(/_/g, " ").toUpperCase()}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-[10px] uppercase tracking-widest">
                            <div className={found.has_license ? "text-[#16a34a]" : "text-[#E2231A]"}>{found.has_license ? "✓" : "✗"} Patente</div>
                            <div className={found.manleva_signed ? "text-[#16a34a]" : "text-[#E2231A]"}>{found.manleva_signed ? "✓" : "✗"} Manleva</div>
                        </div>

                        {found.arrived_at && (
                            <div className="mt-3 bg-[#ca8a04]/10 border border-[#ca8a04]/40 text-[#ca8a04] px-3 py-2 text-xs">
                                ⚠ Già segnato arrivato — {new Date(found.arrived_at).toLocaleString("it-IT")}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 mt-5">
                            <button
                                data-testid="btn-mark-arrived"
                                onClick={markArrived}
                                disabled={busy}
                                className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 text-[#F4F2ED] py-4 font-display text-lg uppercase tracking-widest flex items-center justify-center gap-2"
                            >{busy ? <Loader2 className="animate-spin" size={18} /> : <><CheckCircle2 size={18} /> Arrivato</>}</button>
                            <button
                                data-testid="btn-scan-again"
                                onClick={skipAndScanAgain}
                                className="border border-[#9AA2AD]/40 hover:border-[#E2231A] text-[#F4F2ED] py-4 font-display text-lg uppercase tracking-widest"
                            >Scansiona altro</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
